"use client";

import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { useEffect, useRef, useState } from "react";

import { admitTtvConversionInput } from "@/app/(application)/(normal)/create/components/input/ttv/admitTtvConversionInput";
import { getConversionInputChoices } from "@/app/(application)/(normal)/create/components/input/ttv/getConversionInputChoices";
import { getConversionInputStores } from "@/app/(application)/(normal)/create/components/input/ttv/getConversionInputStores";
import { readTtvConversionSoundIds } from "@/app/(application)/(normal)/create/ttvConversionSoundIds/readTtvConversionSoundIds";
import { rememberTtvConversionSoundIds } from "@/app/(application)/(normal)/create/ttvConversionSoundIds/rememberTtvConversionSoundIds";
import { captureTtvEvent } from "@/app/(application)/(ttv)/ttv/analytics/captureTtvEvent";
import { Button } from "@/components/buttons/Button";
import { Modal } from "@/components/Modal";
import { NormalText } from "@/components/text/NormalText";
import { useConversionIsSubmitting } from "@/lib/zustand/useConversionIsSubmitting";
import type { TtvConversionSources } from "@/types/defs/ttvConversionSource";
import { parseDatesSound } from "@/utils/serverClientSerialization/parseDatesSound";

export function TtvConversionInput({
  source,
}: {
  source: TtvConversionSources;
}) {
  const searchParams = useSearchParams();
  const queryKey = searchParams.getAll("ttvSoundId").join(",");
  const sourceIds =
    source.status === "ready" ?
      source.sounds.map((sound) => sound.id)
    : source.soundIds;
  const isActive = sourceIds.some((id) =>
    searchParams.getAll("ttvSoundId").includes(id)
  );
  const isSubmitting = useConversionIsSubmitting((state) => state.isSubmitting);
  const [isFull, setIsFull] = useState(false);
  const [choices, setChoices] = useState<
    { index: number; label: string; soundId: string | null }[]
  >([]);
  const [replacementIndexes, setReplacementIndexes] = useState<number[]>([]);
  const admittedIds = useRef(new Set<string>());
  const removedIds = useRef(new Set<string>());
  const dismissalPending = useRef(false);
  const isAdmitting = useRef(false);
  const statusRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isActive) {
      statusRef.current?.scrollIntoView({ block: "center" });
    }
  }, [isActive, queryKey]);

  const removeFromUrl = (ids: string[]) => {
    ids.forEach((id) => removedIds.current.add(id));
    const params = new URLSearchParams(window.location.search);
    const remaining = params
      .getAll("ttvSoundId")
      .filter((id) => !removedIds.current.has(id));
    params.delete("ttvSoundId");
    remaining.forEach((id) => params.append("ttvSoundId", id));
    // Removing an already-loaded input only changes the deep link. Update it synchronously: overlapping server navigations can otherwise finish out of order and resurrect a removed ID. Next's history integration also updates useSearchParams without refetching the inputs.
    window.history.replaceState(
      null,
      "",
      `/create${params.size > 0 ? `?${params.toString()}` : ""}${window.location.hash}`
    );
  };
  const dismiss = () => {
    if (dismissalPending.current) {
      return;
    }
    dismissalPending.current = true;
    setIsFull(false);
    removeFromUrl(sourceIds);
  };
  const getRequestedSounds = () =>
    source.status === "ready" ?
      source.sounds.filter(
        (sound) =>
          searchParams.getAll("ttvSoundId").includes(sound.id) &&
          !removedIds.current.has(sound.id)
      )
    : [];
  const admit = (replacements: number[]) => {
    const requested = getRequestedSounds();
    if (
      requested.length === 0 ||
      useConversionIsSubmitting.getState().isSubmitting
    ) {
      return false;
    }
    isAdmitting.current = true;
    try {
      if (
        !admitTtvConversionInput(requested.map(parseDatesSound), replacements)
      ) {
        return false;
      }
      // Output reads this to count a finished conversion of these takes as the funnel's outcome.
      rememberTtvConversionSoundIds(
        requested.map((sound) => sound.id),
        null
      );
      const transfers = readTtvConversionSoundIds();
      requested.forEach((sound) => {
        if (!admittedIds.current.has(sound.id)) {
          captureTtvEvent({
            event: "ttv_convert_input_ready",
            soundId: sound.id,
            transferId: transfers.get(sound.id) ?? null,
          });
        }
        admittedIds.current.add(sound.id);
      });
      setIsFull(false);
      setChoices(getConversionInputChoices());
      setReplacementIndexes([]);
      return true;
    } finally {
      isAdmitting.current = false;
    }
  };

  useEffect(() => {
    if (!isActive || source.status !== "ready" || dismissalPending.current) {
      return;
    }
    const stores = getConversionInputStores();
    const refresh = () => {
      if (isAdmitting.current) {
        return;
      }
      const active = getConversionInputChoices();
      setChoices(active);
      const removed = [...admittedIds.current].filter(
        (id) =>
          !removedIds.current.has(id) &&
          !active.some((choice) => choice.soundId === id)
      );
      if (removed.length > 0) {
        removeFromUrl(removed);
      }
    };
    // Detect removals before any retry, including inputs cleared while a conversion submission was in flight.
    refresh();
    if (!isSubmitting && getRequestedSounds().length > 0) {
      setIsFull(!admit([]));
    }
    refresh();
    const unsubscribe = stores.map((store) => store.subscribe(refresh));
    return () => unsubscribe.forEach((stop) => stop());
  }, [source, queryKey, isActive, isSubmitting]);

  if (!isActive || dismissalPending.current) {
    return null;
  }
  if (source.status !== "ready") {
    return (
      <div
        ref={statusRef}
        role="alert"
        className="my-3 rounded-lg bg-primaryLight p-3"
      >
        <NormalText elementType="p">
          {source.status === "signedOut" ?
            "Sign in to open your takes from Text to vocals."
          : "These takes are no longer available. Open Text to vocals and send them again."
          }
        </NormalText>
        <Link
          href="/text-to-vocals"
          className="text-sm font-semibold text-primary underline"
        >
          Open Text to vocals
        </Link>
        <Button variant="outline" size="xs" onClick={dismiss} className="ml-3">
          Dismiss
        </Button>
      </div>
    );
  }
  const requested = getRequestedSounds();
  if (requested.length === 0) {
    return null;
  }
  const missingCount = requested.filter(
    (sound) => !choices.some((choice) => choice.soundId === sound.id)
  ).length;
  const freeCount = getConversionInputStores().length - choices.length;
  const replacementsNeeded = Math.max(0, missingCount - freeCount);
  const replaceable = choices.filter(
    (choice) => !requested.some((sound) => sound.id === choice.soundId)
  );
  const replacements = replacementIndexes.filter((index) =>
    replaceable.some((choice) => choice.index === index)
  );
  const hasOtherInputs = choices.some(
    (choice) => !requested.some((sound) => sound.id === choice.soundId)
  );
  // A submission that started between the effect's check and admission leaves the list "full" with nothing to replace; the effect admits the takes once it finishes.
  const isReplacementNeeded = isFull && replacementsNeeded > 0;
  let message = "Your takes are ready. Choose a voice, then click Convert.";
  if (isSubmitting) {
    message =
      "Your conversion is submitting. New takes will be added when submission finishes.";
  } else if (isReplacementNeeded) {
    message = "Choose which inputs to replace, or cancel adding these takes.";
  } else if (hasOtherInputs) {
    message =
      "Your other inputs are still included. Convert will process all listed vocals with the selected voice.";
  }
  return (
    <>
      <div
        ref={statusRef}
        role="status"
        className="my-3 rounded-lg bg-primaryLight p-3"
      >
        <NormalText elementType="p">
          <strong>From Text to vocals</strong> · {requested.length}{" "}
          {requested.length === 1 ? "take" : "takes"}
        </NormalText>
        <NormalText elementType="p">{message}</NormalText>
      </div>
      <Modal
        open={isReplacementNeeded}
        closeModal={dismiss}
        title="Make room for your takes"
        widthMode="normal"
      >
        <NormalText elementType="p" className="mb-4">
          Select {replacementsNeeded}{" "}
          {replacementsNeeded === 1 ? "input" : "inputs"} to replace. Your other
          inputs will stay.
        </NormalText>
        <div className="flex flex-col gap-3">
          {replaceable.map((choice) => (
            <label
              key={choice.index}
              className="flex items-center gap-3 text-sm"
            >
              <input
                type="checkbox"
                checked={replacements.includes(choice.index)}
                disabled={
                  isSubmitting ||
                  (!replacements.includes(choice.index) &&
                    replacements.length >= replacementsNeeded)
                }
                onChange={() =>
                  setReplacementIndexes(
                    replacements.includes(choice.index) ?
                      replacements.filter((index) => index !== choice.index)
                    : [...replacements, choice.index]
                  )
                }
                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span className="min-w-0 break-words">
                Replace {choice.label}
              </span>
            </label>
          ))}
          <Button
            variant="primary"
            disabled={
              isSubmitting || replacements.length !== replacementsNeeded
            }
            onClick={() => {
              admit(replacements);
            }}
          >
            Add {requested.length} {requested.length === 1 ? "take" : "takes"}
          </Button>
          <Button variant="outline" onClick={dismiss}>
            Cancel
          </Button>
        </div>
      </Modal>
    </>
  );
}
